/**
 * Wall Archaeology ✦ iter212
 * ─────────────────────────────────────────────────────────────
 * /debug/wall-archaeology/:slug
 *
 * Time-excavation, NOT a video player. The wall remembers itself.
 *
 * Architectural rule (Craig, iter212):
 *
 *   Replay reconstruction MUST use the SAME renderer pipeline as
 *   live/projector. We mount <LiveRememberUsPage replayMode /> and
 *   layer a quiet, interaction-revealed timeline on top. No
 *   separate archaeology renderer. Ever.
 *
 *   • Autoplays at ~50× the moment the page loads.
 *   • Timeline appears only on interaction (mouse-move / touch).
 *   • Auto-hides after ~2.5s of silence.
 *   • No play/pause icons. No technical timestamps. No chrome.
 *   • Anchors read: "early evening ✦ midnight ✦ final moments".
 *   • The strokes ARE the motion; the timeline is a faint
 *     archival eyebrow, never a control surface.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import LiveRememberUsPage from '../LiveRememberUsPage';

const REVEAL_HIDE_MS = 2_500;

const WallArchaeologyPage = () => {
  const [progress, setProgress] = useState({ delivered: 0, total: 0, complete: false });
  const [revealed, setRevealed] = useState(false);
  const hideTimerRef = useRef(null);

  const onReplayProgress = useCallback((p) => {
    setProgress((prev) => ({ ...prev, ...p }));
  }, []);

  // Reveal the timeline on ANY interaction; auto-hide after silence.
  useEffect(() => {
    const onInteract = () => {
      setRevealed(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setRevealed(false), REVEAL_HIDE_MS);
    };
    window.addEventListener('mousemove', onInteract, { passive: true });
    window.addEventListener('touchstart', onInteract, { passive: true });
    window.addEventListener('keydown', onInteract);
    return () => {
      window.removeEventListener('mousemove', onInteract);
      window.removeEventListener('touchstart', onInteract);
      window.removeEventListener('keydown', onInteract);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // ── Progress → archival anchor ────────────────────────────
  // No technical timestamps, no percentages. Just three quiet
  // anchors that describe where in the night you are.
  const ratio = progress.total > 0 ? Math.min(1, progress.delivered / progress.total) : 0;
  let anchor = 'early evening ✦';
  if (ratio > 0.66) anchor = 'final moments ✦';
  else if (ratio > 0.33) anchor = 'midnight ✦';

  return (
    <div data-testid="wall-archaeology-shell" style={{ position: 'fixed', inset: 0, background: '#050402' }}>
      {/* The SAME renderer the projector uses — replayMode swaps
          the live SSE for the replay-stream and starts the ledger
          empty. Everything else (glow lifecycle, drift, chalk
          dust, density theming) is identical. */}
      <LiveRememberUsPage
        replayMode
        replaySpeedMul={50}
        onReplayProgress={onReplayProgress}
      />

      {/* Archival timeline — interaction-revealed, never a control.
          Sits as a thin eyebrow at the bottom. Pointer-events off
          so it never competes with the wall. */}
      <div
        data-testid="archaeology-timeline"
        data-revealed={revealed ? '1' : '0'}
        style={{
          position: 'fixed', left: '4vw', right: '4vw', bottom: '3vh',
          pointerEvents: 'none',
          opacity: revealed ? 1 : 0,
          transition: 'opacity 1.2s ease-in-out',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          fontFamily: 'Georgia, "Cormorant Garamond", serif',
          fontStyle: 'italic',
          color: 'rgba(248,232,200,0.45)',
          fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
          marginBottom: 8,
        }}>
          <span data-testid="archaeology-anchor-start">early evening ✦</span>
          <span data-testid="archaeology-anchor-mid">midnight ✦</span>
          <span data-testid="archaeology-anchor-end">final moments ✦</span>
        </div>
        {/* A thin, ALMOST-invisible progress trace — never a fill
            bar, never a scrubber handle. The wall is the timeline. */}
        <div style={{
          position: 'relative', height: 1,
          background: 'rgba(248,232,200,0.08)',
        }}>
          <div
            data-testid="archaeology-progress-trace"
            style={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: `${Math.round(ratio * 100)}%`,
              background: 'rgba(193,154,75,0.45)',
              transition: 'width 0.6s ease-out',
            }}
          />
        </div>
        <p style={{
          margin: '10px 0 0', textAlign: 'center',
          fontFamily: 'Georgia, serif', fontStyle: 'italic',
          color: 'rgba(248,232,200,0.4)', fontSize: 11,
          letterSpacing: '0.06em',
        }} data-testid="archaeology-current-anchor">
          {anchor}
        </p>
      </div>
    </div>
  );
};

export default WallArchaeologyPage;
